var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import AbstractService from '../abstract-service';
import CoreException from '@vertical-plus/vue-js-api-platform/dist/model/exception/exception';
import Event from '../../event/event';
import EventBus from '@vertical-plus/vue-js-core/dist/util/event/event-bus';
import Exception from '../../exception/exception';
import GetUserSettingsTask from '../../../model/task/organisation/get-user-settings-task';
import GetUserTask from '../../../model/task/organisation/get-user-task';
import LoginGoogleTask from '@vertical-plus/vue-js-api-platform/dist/model/task/authentication/login-google-task';
import LoginTask from '@vertical-plus/vue-js-api-platform/dist/model/task/authentication/login-task';
import PasswordResetTask from '@vertical-plus/vue-js-api-platform/dist/model/task/authentication/password-reset-task';
import RefreshTokenTask from '@vertical-plus/vue-js-api-platform/dist/model/task/authentication/refresh-token-task';
import RegisterTask from '@vertical-plus/vue-js-api-platform/dist/model/task/authentication/register-task';
import RegistrationTokenTask from '@vertical-plus/vue-js-api-platform/dist/model/task/authentication/registration-token-task';
import ResetPasswordTask from '@vertical-plus/vue-js-api-platform/dist/model/task/authentication/reset-password-task';
import Util from '@vertical-plus/vue-js-core/dist/util/util';
export default class AuthenticationService extends AbstractService {
    /**
     * Registers a new user
     *
     * @param firstName
     * @param lastName
     * @param email
     * @param password
     */
    register(firstName, lastName, email, password) {
        return __awaiter(this, void 0, void 0, function* () {
            // Get a registration token
            const registrationTokenTask = new RegistrationTokenTask();
            const registrationToken = yield registrationTokenTask.run();
            // Register the user
            const task = new RegisterTask();
            task.firstName = firstName;
            task.lastName = lastName;
            task.email = email;
            task.password = password;
            task.token = registrationToken;
            yield task.run();
        });
    }
    /**
     * Requests a password reset
     *
     * @param email
     */
    passwordResetRequest(email) {
        return __awaiter(this, void 0, void 0, function* () {
            const task = new PasswordResetTask();
            task.email = email;
            yield task.run();
        });
    }
    /**
     * Resets a user's password
     *
     * @param token
     * @param password
     */
    passwordReset(token, password) {
        return __awaiter(this, void 0, void 0, function* () {
            const task = new ResetPasswordTask();
            task.token = token;
            task.password = password;
            yield task.run();
        });
    }
    /**
     * Attempts login, returning the logged in user if successful
     *
     * @param email
     * @param password
     * @returns
     */
    login(email, password) {
        return __awaiter(this, void 0, void 0, function* () {
            // Attempt to login with the passed details
            const task = new LoginTask();
            task.email = email;
            task.password = password;
            // Mark as not oauth login
            this.store.commit('authentication/oauthLogin', false);
            // Update credentials
            const credentials = yield task.run();
            yield this.updateCredentials(credentials);
            // Update and return the current user
            return this.updateUser(credentials.userId);
        });
    }
    /**
     * Attempts Google login, returning the logged in user if successful
     *
     * @param token
     * @param code
     * @returns
     */
    loginGoogle(token = null, code = null) {
        return __awaiter(this, void 0, void 0, function* () {
            // Confirm Google login
            const task = new LoginGoogleTask();
            task.code = code;
            task.token = token;
            // Mark as oauth login
            this.store.commit('authentication/oauthLogin', true);
            // Update credentials
            const credentials = yield task.run();
            yield this.updateCredentials(credentials);
            // Update and return the current user
            return this.updateUser(credentials.userId);
        });
    }
    /**
     * Logs the current user out
     */
    logout() {
        return __awaiter(this, void 0, void 0, function* () {
            this.store.commit('authentication/token', null);
            this.store.commit('authentication/refreshToken', null);
            this.store.commit('authentication/refreshingToken', false);
            this.store.commit('authentication/oauthLogin', false);
            this.store.commit('authentication/user', null);
            this.store.commit('authentication/userSettings', []);
            this.store.commit('authentication/project', null);
            this.store.commit('authentication/projectSettings', []);
        });
    }
    /**
     * Refreshes the authentication token
     */
    refreshToken() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                // Check for other running refresh token requests
                if (this.store.getters['authentication/refreshingToken']) {
                    // Wait until the running request has finished
                    while (this.store.getters['authentication/refreshingToken']) {
                        yield Util.sleep(50);
                    }
                    // Simply return out as the previous request will either have been successful or would have logged the user out
                    return;
                }
                // Mark that we are refreshing the token to prevent duplicate requests
                this.store.commit('authentication/refreshingToken', true);
                // Attempt to renew the token using the refresh token
                const task = new RefreshTokenTask();
                task.refreshToken = this.store.getters['authentication/refreshToken'];
                // Update credentials
                const credentials = yield task.run();
                yield this.updateCredentials(credentials);
                // Update the current user
                yield this.updateUser(credentials.userId);
                // Mark as no longer refreshing the token
                this.store.commit('authentication/refreshingToken', false);
            }
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            catch (error) {
                switch (error.message) {
                    case CoreException.RefreshTokenExpired:
                        // Send a message to indicate the session has expired
                        EventBus.instance.$emit(Event.SessionExpired);
                        // Log the user out
                        this.logout();
                        // Mark as no longer refreshing the token
                        this.store.commit('authentication/refreshingToken', false);
                        break;
                    default:
                        // Mark as no longer refreshing the token
                        this.store.commit('authentication/refreshingToken', false);
                        throw error;
                }
            }
        });
    }
    /**
     * Updates the credentials in the session
     *
     * @param credentials
     */
    updateCredentials(credentials) {
        return __awaiter(this, void 0, void 0, function* () {
            this.store.commit('authentication/token', credentials.token);
            this.store.commit('authentication/refreshToken', credentials.refreshToken);
            this.store.commit('authentication/refreshingToken', false);
        });
    }
    /**
     * Updates the current user
     *
     * @param id
     * @returns
     */
    updateUser(id) {
        return __awaiter(this, void 0, void 0, function* () {
            // Load the details of the logged in user
            const userTask = new GetUserTask();
            userTask.token = this.token;
            userTask.id = id;
            const user = yield userTask.run();
            if (!user) {
                throw new Error(Exception.UserNotFound);
            }
            // Store the user
            this.store.commit('authentication/user', user);
            // Load the user settings
            const userSettingsTask = new GetUserSettingsTask();
            userSettingsTask.token = this.token;
            userSettingsTask.userId = id;
            const userSettings = yield userSettingsTask.run();
            // Store the user settings
            this.store.commit('authentication/userSettings', userSettings);
            // Return the logged in user
            return user;
        });
    }
}
//# sourceMappingURL=authentication-service.js.map